import { intersection, mergeWith, uniq } from 'lodash/fp';

import { AGE_MAX_KEY, AGE_MIN_KEY } from '../models/data-key';
import { DataGranularity } from '../types';

const mergeGranularityPair = (
    granularity1: DataGranularity = {},
    granularity2: DataGranularity = {}
) =>
    mergeWith(
        (obj, src) => {
            if (Array.isArray(obj) && Array.isArray(src)) {
                return uniq(obj.concat(src));
            }
        },
        granularity1,
        granularity2
    );

export const mergeGranularity = (...granularities: DataGranularity[]): DataGranularity =>
    granularities.reduce<DataGranularity>(
        (merged, granularity) => mergeGranularityPair(merged, granularity),
        {}
    );

const NARROW_SCALAR_BOUND = {
    [AGE_MIN_KEY]: Math.max,
    [AGE_MAX_KEY]: Math.min,
};

const intersectGranularityPair = (
    granularity1: DataGranularity,
    granularity2: DataGranularity = {}
): DataGranularity => {
    const intersected: DataGranularity = { ...granularity1 };

    Object.entries(granularity2).forEach(([key, value]) => {
        const current = intersected[key];

        if (current === undefined) {
            intersected[key] = Array.isArray(value) ? [...value] : value;
        } else if (Array.isArray(current) && Array.isArray(value)) {
            intersected[key] = intersection(current, value);
        } else if (NARROW_SCALAR_BOUND[key]) {
            intersected[key] = NARROW_SCALAR_BOUND[key](current, value);
        } else {
            intersected[key] = value;
        }
    });

    return intersected;
};

export const intersectGranularity = (...granularities: DataGranularity[]): DataGranularity =>
    granularities
        .filter(Boolean)
        .reduce<DataGranularity>(
            (intersected, granularity) => intersectGranularityPair(intersected, granularity),
            {}
        );
